import React, { useState } from "react";
import { Mic, Square, Loader2, MicOff } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import AudioVisualizer from "./AudioVisualizer";

interface VoiceRecordButtonProps {
  isRecording: boolean;
  isProcessing: boolean;
  duration: number;
  analyserNode: AnalyserNode | null;
  showNoResults?: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

const VoiceRecordButton: React.FC<VoiceRecordButtonProps> = ({
  isRecording,
  isProcessing,
  duration,
  analyserNode,
  showNoResults = false,
  onStartRecording,
  onStopRecording,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (isProcessing) return;
    if (isRecording) {
      onStopRecording();
    } else {
      onStartRecording();
    }
  };

  // --- Recording mode: morphed pill with visualization ---
  if (isRecording) {
    return (
      <button
        onClick={handleClick}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsActive(false);
          setIsHovered(false);
        }}
        className="relative flex items-center gap-2.5 bg-warm-bg-card border border-warm-border shadow-soft-md overflow-hidden animate-recording-pill-enter"
        style={{
          height: 52,
          paddingLeft: 12,
          paddingRight: 14,
          borderRadius: 26,
          transform: isActive ? "scale(0.97)" : isHovered ? "scale(1.02)" : "scale(1)",
          transition: "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        aria-label="Stop recording"
      >
        {/* Stop icon */}
        <div className="relative flex-shrink-0 w-7 h-7 rounded-full bg-warm-bg-elevated flex items-center justify-center">
          <Square
            className="fill-current"
            size={10}
            style={{ color: "var(--color-recording)" }}
          />
        </div>

        {/* Waveform visualization */}
        <div className="flex-1 min-w-[100px] h-8 relative overflow-hidden">
          {analyserNode && <AudioVisualizer analyserNode={analyserNode} />}
        </div>

        {/* Duration */}
        <span className="flex-shrink-0 text-xs font-mono text-warm-text-secondary tabular-nums">
          {formatDuration(duration)}
        </span>
      </button>
    );
  }

  // --- No results mode: morphed pill with message ---
  if (showNoResults) {
    return (
      <div
        className="flex items-center gap-2.5 bg-warm-bg-card border border-warm-border shadow-soft-md animate-recording-pill-enter"
        style={{
          height: 52,
          paddingLeft: 12,
          paddingRight: 14,
          borderRadius: 26,
        }}
        role="status"
      >
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-warm-bg-elevated flex items-center justify-center">
          <MicOff size={14} className="text-warm-text-tertiary" />
        </div>
        <span className="text-sm text-warm-text-secondary whitespace-nowrap">
          Couldn't identify any tasks
        </span>
      </div>
    );
  }

  // --- Idle / Processing mode: round button ---
  let tooltipContent = "";
  if (!isProcessing) {
    tooltipContent = "Record Voice (V)";
  }

  let buttonClass = "";
  if (isProcessing) {
    buttonClass = "bg-warm-blue opacity-70 cursor-not-allowed";
  } else {
    buttonClass = "bg-warm-blue hover:bg-warm-blue-dark";
  }

  return (
    <Tooltip
      content={tooltipContent}
      placement="top"
      align="end"
      disabled={isProcessing}
    >
      <button
        onClick={handleClick}
        onMouseDown={() => !isProcessing && setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onMouseEnter={() => !isProcessing && setIsHovered(true)}
        onMouseLeave={() => {
          setIsActive(false);
          setIsHovered(false);
        }}
        onFocus={() => !isProcessing && setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        disabled={isProcessing}
        data-shortcut="V"
        data-shortcut-shape="circle"
        className={`group relative w-14 h-14 md:w-16 md:h-16 rounded-full shadow-soft-lg hover:shadow-soft-md transition-all text-white ${buttonClass}`}
        style={{
          transform: isActive && !isProcessing ? "scale(0.95)" : isHovered && !isProcessing ? "scale(1.05)" : "scale(1)",
        }}
      >
        {isProcessing ? (
          <Loader2 className="absolute inset-0 m-auto animate-spin" size={24} />
        ) : (
          <Mic className="absolute inset-0 m-auto" size={24} strokeWidth={2.5} />
        )}
      </button>
    </Tooltip>
  );
};

export default VoiceRecordButton;
